import { useEffect, useState, useRef } from "react";
import { Button, Typography, Card, Box, TextField, Grid } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import PageHeader from "src/@core/components/page-header";
import Translations from "src/layouts/components/Translations";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { DefaultPaginationSettings } from "src/constants/general.const";
import { toastError, toastSuccess } from "src/utils/utils";
import TableModules from "src/views/tables/TableModules";
import DialogAddModule from "src/views/dialog/DialogAddModule";
import DialogUserStatusUpdate from "src/views/dialog/DialogUserStatusUpdate";
import DialogConfirmation from "src/views/dialog/DialogConfirmation";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from "src/hooks/useAuth";
import { canCreate, canUpdate, canRemove } from "src/utils/permissions";

const ModulesPage = () => {
  const { givenPermission, user } = useAuth();
  const { courseId } = useParams();
  const navigate = useNavigate();
  const searchTimeoutRef = useRef();
  const [loading, setLoading] = useState(false);
  const [modules, setModules] = useState([]);
  const [search, setSearch] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(
    DefaultPaginationSettings.ROWS_PER_PAGE
  );
  const [moduleDialogOpen, setModuleDialogOpen] = useState(false);
  const [moduleToEdit, setModuleToEdit] = useState(null);
  const [dialogMode, setDialogMode] = useState("add");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState(null);

  // Check course permissions (modules inherit course permissions)
  const canCreateModule = canCreate(givenPermission, "/courses", user?.user_type);
  const canUpdateModule = canUpdate(givenPermission, "/courses", user?.user_type);
  const canDeleteModule = canRemove(givenPermission, "/courses", user?.user_type);

  const toggleConfirm = (e, module = null) => {
    setConfirmOpen((prev) => !prev);
    setModuleToDelete(module);
  };

  const handleAddModule = () => {
    setDialogMode("add");
    setModuleToEdit(null);
    setModuleDialogOpen(true);
  };

  const handleEditModule = (e, module) => {
    setDialogMode("edit");
    setModuleToEdit(module);
    setModuleDialogOpen(true);
  };

  const handleModuleDialogClose = () => {
    setModuleDialogOpen(false);
    setModuleToEdit(null);
  };

  const fetchData = ({ currentPage, pageSize, search }) => {
    setLoading(true);
    const params = { page: currentPage, limit: pageSize, search };

    axiosInstance
      .get(ApiEndPoints.MODULES.list(courseId), { params })
      .then((response) => {
        setModules(response.data?.data?.modules || []);
        setTotalCount(response.data?.data?.totalRecords || 0);
      })
      .catch((error) => toastError(error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (courseId) {
      fetchData({ currentPage, pageSize, search });
    }
  }, [currentPage, pageSize, search, courseId]);

  const handleSearchChange = (e) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      setSearch(e.target.value);
      setCurrentPage(1);
    }, 500);
  };

  const onConfirmDelete = () => {
    axiosInstance
      .delete(ApiEndPoints.MODULES.delete(moduleToDelete?.id))
      .then((response) => {
        toastSuccess(response.data?.message || "Module deleted successfully");
        fetchData({ currentPage, pageSize, search });
        setConfirmOpen(false);
        setModuleToDelete(null);
      })
      .catch((error) => {
        toastError(error);
      });
  };

  return (
    <>
      <Grid container spacing={4} padding={2}>
        <Grid item xs={12}>
          <Box sx={{
            p: 5,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}>
            <Box onClick={() => navigate(-1)} sx={{ cursor: "pointer" }}>
              <ArrowBackIcon />
            </Box>
            <PageHeader
              title={
                <Typography variant="fm-h5">
                  <Translations text="Modules" />
                </Typography>
              }
              action={
                canCreateModule ? (
                  <Button
                    variant="contained"
                    onClick={handleAddModule}
                  >
                    Add Module
                  </Button>
                ) : null
              }
            />
          </Box>
          <Card>
            <Box
              sx={{
                p: 5,
                pb: 0,
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "flex-end"
              }}
            >
              <Box
                sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end" }}
              >
                <TextField
                  type="search"
                  size="small"
                  placeholder="Search"
                  onChange={handleSearchChange}
                />
              </Box>
            </Box>
            <Box sx={{ p: 5 }}>
              <TableModules
                rows={modules}
                loading={loading}
                totalCount={totalCount}
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                setPageSize={setPageSize}
                pageSize={pageSize}
                toggleEdit={handleEditModule}
                toggleDelete={toggleConfirm}
                canEdit={canUpdateModule}
                canDelete={canDeleteModule}
              />
            </Box>
          </Card>
        </Grid>
      </Grid>

      <DialogAddModule
        open={moduleDialogOpen}
        onClose={handleModuleDialogClose}
        onSuccess={() => {
          fetchData({
            currentPage: currentPage,
            pageSize: pageSize,
            search: search,
          });
        }}
        mode={dialogMode}
        dataToEdit={moduleToEdit}
        courseId={courseId}
      />


      <DialogConfirmation
        open={confirmOpen}
        toggle={toggleConfirm}
        title="Delete Module"
        subtitle="Are you sure you want to delete this module?"
        onConfirm={onConfirmDelete}
      />
    </>
  );
};

export default ModulesPage;
